import React from 'react';
import CopyElement from '../../../../../../ComponentsV1/CopyElement/CopyElement';
import Button from '../../../../../../ComponentsV1/Button';
import { ResolutionStatusIcon } from '../constants';
import { Clock9 } from 'lucide-react';
import { Skeleton } from '../../../../../../ComponentsV1/Skeleton/Skeleton';
import { useEffect } from 'react';
import { RAProceedBtnClickEvent } from '../../../../../../../analytics/reso-assist/events';
import { MetricsForJumboEvent } from '../../../../../../../analytics/constants';
import { useWidgetAnimation } from './useWidgetAnimation';

const ResoAssistResolutionWidget = ({
  orderId,
  userId,
  cartId,
  complaintType,
  resoAssistResolutionDetails,
  handleResoAssistProceed,
  isStateUpdating = false,
  resoAssistDetails = {},
}) => {
  const animClass = useWidgetAnimation('widget-animate-3');
  const {
    is_btn_visibile: isBtnVisible = false,
    enable_resolution: enableResolution,
    header_text: headerText,
    is_verified: isVerified,
    unverified_display_text: unverifiedDisplayText,
    preferred_resolution: preferredResolution,
  } = resoAssistResolutionDetails || {};

  const ResoHeaderIcon = ResolutionStatusIcon[headerText?.toLowerCase()]
    ?.icon || <Clock9 size={18} />;

  const ResoIconStyles =
    ResolutionStatusIcon[headerText?.toLowerCase()]?.styles ||
    'tw-bg-teal-100 tw-border-teal-200/50';

  useEffect(() => {
    if (headerText?.toLowerCase() === 'service denied') {
      const payload = {
        [MetricsForJumboEvent.order_id]: orderId,
        [MetricsForJumboEvent.cart_id]: cartId,
        [MetricsForJumboEvent.customer_id]: userId,
        [MetricsForJumboEvent.feature_meta]: {
          'dataForEvaluate.complaint_type': complaintType,
          is_service_denied: true,
          'resoAssistDetails.dp_calling_details':
            resoAssistDetails?.dp_calling_details?.call_status?.text,
          'resoAssistDetails.sm_calling_details':
            resoAssistDetails?.sm_calling_details?.call_status?.text,
          'resoAssistResolutionDetails.reso_assist_summary':
            resoAssistDetails?.reso_assist_summary?.text,
        },
      };
      RAProceedBtnClickEvent(payload);
    }
  }, [headerText, orderId, cartId, userId, complaintType]);

  return (
    <div
      className={`tw-flex-col tw-border-solid tw-border-[1px] tw-rounded-[8px] tw-border-grey-200 tw-h-full tw-p-3 tw-bg-grey-100/30 tw-w-full ${animClass}
        ${isStateUpdating ? 'tw-pointer-events-none !tw-opacity-60' : ''}
        `}
    >
      {/* Header */}
      <div className='tw-flex tw-gap-3 tw-items-center tw-mb-4'>
        <div
          className={`tw-h-full tw-aspect-square  tw-rounded-full tw-justify-center tw-items-center tw-border-solid tw-w-16 tw-border-[1px] ${
            isStateUpdating
              ? 'tw-bg-teal-100 tw-border-teal-200/50'
              : ResoIconStyles
          }`}
        >
          {isStateUpdating ? <Clock9 size={18} /> : ResoHeaderIcon}
        </div>
        <div className='tw-flex-1 tw-flex-col tw-leading-none'>
          <div className='tw-text-[1.5rem] tw-text-zinc-600 tw-font-medium'>
            {isStateUpdating ? 'Updating...' : headerText}
          </div>
        </div>
      </div>

      {/* Preferred resolution details */}
      {isStateUpdating ? null : (
        <div
          className={`tw-flex-col tw-h-full tw-gap-1 tw-w-full tw-px-2 ${
            isBtnVisible || headerText.toLowerCase() === 'service denied'
              ? 'tw-mb-4'
              : ''
          }`}
        >
          {isVerified ? (
            <>
              <div className='tw-text-[1.4rem] tw-text-zinc-600 tw-font-medium tw-leading-tight'>
                {preferredResolution?.text}
              </div>
              {headerText.toLowerCase() !== 'service denied' && (
                <div className='tw-text-[1.3rem] tw-text-zinc-500 tw-font-medium tw-leading-tight tw-mt-1'>
                  {preferredResolution?.subtext}
                </div>
              )}
            </>
          ) : (
            <div className='tw-text-[1.4rem] tw-text-zinc-600 tw-font-medium tw-leading-tight'>
              {unverifiedDisplayText}
            </div>
          )}
        </div>
      )}

      {/* Copiable Comm */}
      {headerText.toLowerCase() === 'service denied' &&
      !isBtnVisible &&
      !isStateUpdating ? (
        <div
          className={`tw-flex-col tw-border-solid tw-border-[1px] tw-rounded-[8px] tw-border-grey-200 tw-h-full tw-p-3 tw-w-full`}
        >
          <CopyElement
            titleText={
              'Were unable to assist with this concern at the moment. Please email us at info@blinkit.com with your order details, and our team will review your case.'
            }
            title={
              <div className='tw-text-left tw-font-medium tw-text-[1.3rem] tw-text-zinc-500 tw-leading-tight'>
                {preferredResolution?.subtext}
              </div>
            }
            className='tw-w-[95%]'
          />
        </div>
      ) : null}

      {/* Proceed with resolution button */}
      {isBtnVisible && (
        <Button
          onClick={handleResoAssistProceed}
          title={'Proceed'}
          visibility={!isStateUpdating}
          disabled={!enableResolution}
          data-dd-action-name='Reso Assist Proceed'
        />
      )}

      {isStateUpdating ? (
        <Skeleton
          rows={[
            { cols: 1, height: '3rem' },
            { cols: 1, height: '2rem' },
          ]}
        />
      ) : null}
    </div>
  );
};

export default ResoAssistResolutionWidget;
